/**
 * Who handles an Escape pressed inside a dialog or sheet: the dialog, or a
 * control inside it that closes on Escape itself (an editor's typeahead menu,
 * the link field). Radix catches Escape on the document before the control
 * sees it, so without this the first Escape closed the whole dialog
 * (BRE-8225, BRE-8336). Import-free, so the dialog and sheet primitives carry
 * no editor code: an editor module registers its check when it loads.
 */

/** Marks an element whose own Escape handler must run instead of an enclosing
 *  dialog's or sheet's (the bubble toolbar's link field). */
export const OWNS_ESCAPE_ATTRIBUTE = "data-owns-escape";

/** Returns true when it took this Escape; the dialog then stays open. */
export type EscapeOwner = (event: KeyboardEvent) => boolean;

const owners = new Set<EscapeOwner>();

export function registerEscapeOwner(owner: EscapeOwner): void {
  owners.add(owner);
}

/** Wraps a Radix `onEscapeKeyDown` so a control's own Escape never dismisses. */
export function keepEditorEscape(
  handler: ((event: KeyboardEvent) => void) | undefined
): (event: KeyboardEvent) => void {
  return (event) => {
    const ownedByField =
      event.target instanceof Element && event.target.closest(`[${OWNS_ESCAPE_ATTRIBUTE}]`) !== null;
    if (ownedByField || [...owners].some((owner) => owner(event))) {
      event.preventDefault();
      return;
    }
    handler?.(event);
  };
}
